<template>
  <Container :class="$style.paddingZero">
    <LoadingOverlay
      :hasBlur="false"
      :isLight="true"
      :visible="showLoader"
    />
    <HeadingMedium
      hasMargin
      weight="light"
    >
      {{ iframeLabel }}
      <ButtonClose
        :class="$style.closeIcon"
        @click="closeIframe"
      />
    </HeadingMedium>
    <div
      id="appcontainer"
      :class="$style.appContainer"
    />
  </Container>
</template>

<script>
import {
  ButtonClose,
  Container,
  HeadingMedium,
  LoadingOverlay,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import Util from 'client/util/Util';

const googleModule = StoreModuleMap[ItemTypeConsts.mdmGoogle];
const { softwareApps } = ItemTypeConsts;
const softwareAppsModel = StoreModelMap[softwareApps];

export default {
  name: 'AddNewGoogleApp',

  components: {
    ButtonClose,
    Container,
    HeadingMedium,
    LoadingOverlay,
  },

  props: {
    isNewAndroidApp: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      iframeLabel: '',
      route: `/${ItemTypeRoutes[ItemTypeConsts.softwareApps]}`,
      showLoader: true,
    };
  },

  computed: {
    ...mapState(googleModule, [
      'googleEmmEnterpriseObj',
      'packageName',
    ]),

    ...mapState(softwareAppsModel, [
      'item',
    ]),

    ...mapGetters(softwareAppsModel, [
      'isItemBeingCreated',
    ]),
  },

  mounted() {
    this.loadGoogleAppsIframe();
  },

  methods: {
    ...mapActions(googleModule, [
      'updateNewAndroidAppState',
      'getWebToken',
      'updateAddNewAppState',
    ]),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    ...mapActions(softwareAppsModel, [
      'fetchItem',
      'saveItem',
      'setItemStateToCreate',
      'setItemField',
      'setItemSettings',
      'setItemStateToUpdate',
      'setItemGoogleAndroid',
    ]),

    async closeIframe() {
      if (this.isNewAndroidApp) {
        this.$emit('configureComplete');
        this.updateAddNewAppState(false);
        Util.redirect(`${this.route}/google`);
      } else {
        const str = window.location.href;
        const urlSplitList = str.split('/');
        this.setItemField({ key: 'id', value: urlSplitList[6] });
        await this.fetchItem();
        this.$emit('configureComplete');
      }
    },

    async loadGoogleAppsIframe() {
      const webTokenPayload = {
        organizationObjectId: this.googleEmmEnterpriseObj.organizationObjectId,
        enterpriseObjectId: this.googleEmmEnterpriseObj.enterpriseObjectId,
        parentFrameUrl: `https://${window.location.hostname}/#/managedSoftware/google/new`,
      };
      const webTokenResponse = await this.getWebToken(webTokenPayload);
      let iframeUrl = '';

      if (this.isNewAndroidApp) {
        this.iframeLabel = 'Add Application';
        iframeUrl = `https://play.google.com/work/embedded/search?token=${webTokenResponse.value}&mode=SELECT`;
      } else if (
        !this.isNewAndroidApp
        && !this.item.settings[0].googleAndroid.managedConfigurationTemplateId
      ) {
        this.iframeLabel = 'Update Configuration';
        iframeUrl = `https://play.google.com/managed/mcm?token=${webTokenResponse.value}&packageName=${this.packageName}`;
      } else {
        this.iframeLabel = 'Update Configuration';
        iframeUrl = `https://play.google.com/managed/mcm?token=${webTokenResponse.value}&packageName=${this.packageName}&mcmId=${this.item.settings[0].googleAndroid.managedConfigurationTemplateId}&canDelete=true`;
      }

      window.gapi.load('gapi.iframes', () => {
        const options = {
          url: iframeUrl,
          where: document.getElementById('appcontainer'),
          attributes: { style: 'width: 100%; height:100%', scrolling: 'yes' },
        };

        this.showLoader = false;
        const iframe = window.gapi.iframes.getContext().openChild(options);
        iframe.register('onproductselect', (event) => {
          const packageArray = event.packageName.split('.');
          this.setItemStateToCreate(true);
          this.setItemField({ key: 'displayName', value: packageArray[1] });
          this.setItemSettings({ key: 'packageId', value: event.packageName });
          this.setItemSettings({ key: 'packageManager', value: 'GOOGLE_ANDROID' });
          this.setItemSettings({ key: 'enterpriseObjectId', value: this.googleEmmEnterpriseObj.enterpriseObjectId });
          this.saveItem()
            .then(() => {
              this.triggerSuccessNotification({
                title: 'Success',
                message: 'application added successfully',
              });
            })
            .catch((err) => {
              this.triggerErrorNotification({
                title: 'Error',
                message: err.message,
              });
            });
        }, window.gapi.iframes.CROSS_ORIGIN_IFRAMES_FILTER);

        iframe.register('onconfigupdated', (event) => {
          this.setItemGoogleAndroid({ key: 'managedConfigurationTemplateId', value: event.mcmId });
          this.setItemStateToUpdate(true);
          this.saveItem()
            .then(() => {
              this.triggerSuccessNotification({
                title: 'Success',
                message: 'application configuration updated successfully',
              });
            })
            .catch((err) => {
              this.triggerErrorNotification({
                title: 'Error',
                message: err.message,
              });
            })
            .finally(() => {
              this.closeIframe();
            });
        }, window.gapi.iframes.CROSS_ORIGIN_IFRAMES_FILTER);

        iframe.register('onconfigdeleted', () => {
          this.setItemGoogleAndroid({ key: 'managedConfigurationTemplateId', value: '' });
          this.setItemStateToUpdate(true);
          this.saveItem()
            .then(() => {
              this.triggerSuccessNotification({
                title: 'Success',
                message: 'application configuration deleted successfully',
              });
            })
            .catch((err) => {
              this.triggerErrorNotification({
                title: 'Error',
                message: err.message,
              });
            })
            .finally(() => {
              this.closeIframe();
            });
        }, window.gapi.iframes.CROSS_ORIGIN_IFRAMES_FILTER);
      });
    },
  },
};
</script>

<style module>
.appContainer {
  border: var(--jc-border);
  height: 100%;
  padding-bottom: 3.5rem;
  width: 100%;
}

.paddingZero {
  padding-left: 0;
  padding-right: 0;
}

.closeIcon {
  float: right;
  padding-right: var(--jc-spacer-x-small);
}
</style>
